import { FC, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import Popover from "@mui/material/Popover";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import AddIcon from "@mui/icons-material/Add";
import FilterListIcon from "@mui/icons-material/FilterList";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import type { FilterChip } from "@/lib/company-filter";

interface SegmentFiltersControlProps {
  chips: FilterChip[];
  /** Open the filters modal to add/edit a filter. */
  onAddFilter: () => void;
  /** Remove every applied filter. */
  onClearAll: () => void;
  /** Remove a single applied filter (applied immediately). */
  onRemoveChip: (chip: FilterChip) => void;
  /** Open a specific filter in the modal. */
  onChipClick: (chip: FilterChip) => void;
}

/**
 * Compact filters control: a single "Filters · N" button that opens a popover
 * listing the applied filters as removable chips, plus "Add filter" and
 * "Clear all". When no filters are applied it collapses to a plain
 * "Add filter" button. Keeps the toolbar to one row to maximise table space.
 */
export const SegmentFiltersControl: FC<SegmentFiltersControlProps> = ({
  chips,
  onAddFilter,
  onClearAll,
  onRemoveChip,
  onChipClick,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const count = chips.length;

  if (count === 0) {
    return (
      <Button
        size="small"
        startIcon={<AddIcon />}
        onClick={onAddFilter}
        data-testid="open-filters-btn"
        sx={{ textTransform: "none", flexShrink: 0 }}
      >
        Add filter
      </Button>
    );
  }

  return (
    <>
      <Button
        size="small"
        variant="outlined"
        color="primary"
        startIcon={<FilterListIcon fontSize="small" />}
        endIcon={<ArrowDropDownIcon />}
        onClick={(e) => setAnchorEl(e.currentTarget)}
        data-testid="open-filters-btn"
        sx={{
          textTransform: "none",
          flexShrink: 0,
          bgcolor: "background.paper",
          fontWeight: 500,
        }}
      >
        {`Filters · ${count}`}
      </Button>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        slotProps={{ paper: { sx: { mt: 0.5, p: 1.5, width: 360 } } }}
        data-testid="segment-filters-popover"
      >
        <Stack spacing={1.25}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography variant="subtitle2" fontWeight={700}>
              Applied filters
            </Typography>
            <Button
              size="small"
              variant="text"
              onClick={() => {
                onClearAll();
                setAnchorEl(null);
              }}
              data-testid="segment-filters-clear-all"
              sx={{ textTransform: "none" }}
            >
              Clear all
            </Button>
          </Stack>

          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
            {chips.map((chip) => (
              <Chip
                key={chip.id}
                label={chip.label}
                size="small"
                onClick={() => {
                  onChipClick(chip);
                  setAnchorEl(null);
                }}
                onDelete={() => onRemoveChip(chip)}
                sx={{
                  maxWidth: "100%",
                  cursor: "pointer",
                  bgcolor: chip.disabled ? "grey.100" : "primary.lighter",
                  color: chip.disabled ? "text.disabled" : "primary.dark",
                  "& .MuiChip-deleteIcon": {
                    color: chip.disabled ? "text.disabled" : "primary.dark",
                  },
                }}
              />
            ))}
          </Box>

          <Divider />

          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={() => {
              onAddFilter();
              setAnchorEl(null);
            }}
            sx={{ textTransform: "none", alignSelf: "flex-start" }}
          >
            Add filter
          </Button>
        </Stack>
      </Popover>
    </>
  );
};
